import React, { useEffect, useMemo, useState } from 'react';
import { useRBAC, ViewAs } from '../contexts/RBACContext';
import { useTranslation } from '../contexts/TranslationContext';
import { apiService } from '../services/ApiService';
import ModalShell from './shared/ModalShell';

interface Candidate {
  role_id: string; name: string; name_he: string | null; is_site_scoped: boolean;
  holders: number; user_id: string | null; example: string | null;
}
interface Person { id: string; name?: string; email?: string }
interface Choices { roles: Candidate[]; people: Person[] }

export const ViewAsBanner: React.FC = () => {
  const { viewAs, setViewAs } = useRBAC();
  const { t } = useTranslation();
  if (!viewAs) return null;
  return <div role="status" className="dt-view-as-banner">
    <span>{t('va_viewing')} {viewAs.label}</span>
    <span>{t('va_write_notice')}</span>
    <button className="dt-btn sm" onClick={() => setViewAs(null)}>{t('va_exit')}</button>
  </div>;
};

export const ViewAsMenuItem: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { realIsAdmin, viewAs } = useRBAC();
  const { t } = useTranslation();
  if (!realIsAdmin) return null;
  // A row like its neighbours in the account menu (What's new, Log out), not a button among rows.
  return <button type="button" className="dt-header-menu-action" onClick={onOpen}>
    <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true" className="dt-header-ic">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
    </svg>
    <span className="grow">{viewAs ? t('va_change') : t('va_title')}</span>
  </button>;
};

export const ViewAsDialog: React.FC<{
  open: boolean; onClose: () => void; siteId?: string | null; siteName?: string | null;
}> = ({ open, onClose, siteId, siteName }) => {
  const { realIsAdmin, viewAs, setViewAs } = useRBAC();
  const { t, isRTL } = useTranslation();
  const [choices, setChoices] = useState<Choices | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open || !realIsAdmin) return;
    let cancelled = false;
    setChoices(null); setFailed(false);
    apiService.get(`/view-as/candidates${siteId ? `?siteId=${encodeURIComponent(siteId)}` : ''}`)
      .then((result: Choices) => { if (!cancelled) setChoices(result); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [open, realIsAdmin, siteId, retry]);

  const shownPeople = useMemo(() => {
    const q = filter.trim().toLocaleLowerCase();
    return (choices?.people || []).filter(person =>
      `${person.name || ''} ${person.email || ''}`.toLocaleLowerCase().includes(q));
  }, [choices, filter]);

  if (!open || !realIsAdmin) return null;
  const pick = (next: ViewAs) => { setViewAs(next); setFilter(''); onClose(); };

  return <ModalShell ariaLabel={t('va_title')} onClose={onClose} width={560} paddingTop={24} maxHeight="calc(100dvh - 48px)">
    <div className="dt-view-as-dialog">
      <div className="dt-view-as-heading">
        <h2>{t('va_title')}</h2>
        <button className="dt-btn sm" onClick={onClose} aria-label={t('va_close')}>✕</button>
      </div>
      <p className="dt-notebox">{t('va_write_notice')}</p>
      {failed ? <div role="alert" className="dt-warnbox">{t('va_load_error')} <button className="dt-btn sm" onClick={() => setRetry(n => n + 1)}>{t('va_retry')}</button></div>
        : !choices ? <p role="status">{t('va_loading')}</p> : <>
          <section>
            <h3>{t('va_by_role')}{siteName ? ` · ${siteName}` : ''}</h3>
            <p className="dt-view-as-hint">{t('va_role_explanation')}</p>
            {choices.roles.map(role => {
              const label = isRTL ? role.name_he || role.name : role.name;
              const unavailable = role.is_site_scoped && !siteId ? t('va_choose_site')
                : !role.user_id ? (role.is_site_scoped ? t('va_no_site_holder') : t('va_no_holder')) : null;
              return <button key={role.role_id} className="dt-view-as-choice" disabled={Boolean(unavailable)}
                onClick={() => role.user_id && pick({ mode: 'user', userId: role.user_id,
                  label: `${role.example} · ${label}${role.is_site_scoped && siteName ? ` · ${siteName}` : ''}` })}>
                <strong>{label}</strong>
                <span className="dt-view-as-hint">{role.is_site_scoped ? t('va_site_role') : t('va_global_role')}</span>
                <span>{unavailable || `${t('va_via')} ${role.example}`}</span>
              </button>;
            })}
          </section>
          <section>
            <h3>{t('va_by_person')}</h3>
            <p className="dt-view-as-hint">{t('va_person_explanation')}</p>
            <input className="dt-field" type="search" aria-label={t('va_search')} placeholder={t('va_search')} value={filter} onChange={e => setFilter(e.target.value)} />
            {shownPeople.length === 0 && <p>{t('va_no_people')}</p>}
            <div className="dt-view-as-people">
              {shownPeople.map(person => <button key={person.id} className="dt-view-as-choice"
                aria-pressed={viewAs?.mode === 'user' && viewAs.userId === person.id}
                onClick={() => pick({ mode: 'user', userId: person.id, label: person.name || person.email || person.id })}>
                <strong>{person.name || person.email}</strong>
                {person.name && <span className="dt-view-as-hint">{person.email}</span>}
              </button>)}
            </div>
          </section>
        </>}
      {viewAs && <button className="dt-btn sm" onClick={() => { setViewAs(null); onClose(); }}>{t('va_exit')}</button>}
    </div>
  </ModalShell>;
};
export default ViewAsDialog;
